import {formatCoordinates} from '@gaia/utils/formatters';
import mapValues from 'lodash/mapValues';
import uniqueId from 'lodash/uniqueId';

import {ampli} from './trackAnalytics';

interface CreateTrackPoint {
    lngLat: mapboxgl.LngLat;
    track: Track;
    insertBeforePointLocalId?: string;
}
export default function createTrackPoint({
    lngLat,
    track,
    insertBeforePointLocalId,
}: CreateTrackPoint): [string, Track] | undefined {
    if (!track.isGeometryLoaded) return undefined;

    const insertBeforePoint = insertBeforePointLocalId
        ? track.points[insertBeforePointLocalId]
        : undefined;

    if (insertBeforePoint) {
        ampli.insertRouteMidpoint({
            category: 'map2',
            isNewRoute: track.properties.id === undefined,
            routingMode: track.properties.routing_mode ?? 'unspecified',
        });
    }

    const order = insertBeforePoint
        ? insertBeforePoint.properties.order
        : Object.values(track.points).length;

    const localId = uniqueId('point');
    const point: TrackPoint = {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: lngLat.toArray(),
        },
        properties: {
            attr: 'route',
            archived: false,
            isGeometryModified: true,
            writable: true,
            order,
            localId,
            title: formatCoordinates(lngLat.toArray()),
            trackLocalId: track.properties.localId,
            isLocallyCreated: true,
            type: 'waypoint',
        },
    };

    const updatedTrack: Track = {
        ...track,
        points: {
            ...mapValues(track.points, (otherPoint: TrackPoint) => {
                if (otherPoint.properties.archived || otherPoint.properties.deleted) {
                    return otherPoint;
                } else if (otherPoint.properties.order >= order) {
                    return {
                        ...otherPoint,
                        properties: {
                            ...otherPoint.properties,
                            order: otherPoint.properties.order + 1,
                        },
                    };
                } else {
                    return otherPoint;
                }
            }),
            [localId]: point,
        },
    };

    return [localId, updatedTrack];
}

export function createPoint(coordinates: number[], order: number, trackLocalId: string) {
    const localId = uniqueId('point');
    const point: TrackPoint = {
        type: 'Feature',
        geometry: {type: 'Point', coordinates},
        properties: {
            attr: 'route',
            archived: false,
            isGeometryModified: true,
            writable: true,
            order,
            localId,
            title: '',
            trackLocalId: trackLocalId,
            isLocallyCreated: true,
            type: 'waypoint',
        },
    };
    return point;
}
